import React from 'react';
import {
  BadgeCheckIcon,
  EyeIcon,
  IdCardIcon,
  ShieldCheckIcon,
  WalletCardsIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';

const credentials = [
  { label: 'Taxilegitimation', detail: 'Exempel · TL-4821', icon: IdCardIcon },
  { label: 'Föraridentitet', detail: 'Exempeluppgifter', icon: BadgeCheckIcon },
];

export function DigitalIdPreview() {
  return (
    <motion.aside
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      aria-labelledby="digital-id-heading"
      className="mt-5 overflow-hidden rounded-2xl border border-[#0B1120]/10 bg-white shadow-[0_8px_20px_rgba(11,17,32,0.05)]"
    >
      <div className="grid lg:grid-cols-[.9fr_1.1fr]">
        <div className="min-w-0 bg-[#0B1120] p-5 text-white sm:p-6">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[.1em] text-[#CFFAFE]">
            <WalletCardsIcon size={15} aria-hidden="true" /> Digital plånbok
          </div>
          <h3
            id="digital-id-heading"
            className="mt-3 max-w-sm font-display text-2xl leading-[1.12] tracking-[-.035em] sm:text-3xl"
          >
            ID-handlingar nära när dagen behöver gå fort.
          </h3>
          <p className="mt-3 max-w-sm text-sm leading-6 text-slate-300">
            En samlad, lättöverskådlig plats för förarens uppgifter och
            taxilegitimation i den egna arbetsytan.
          </p>
          <div className="mt-5 flex items-start gap-2.5 border-t border-white/10 pt-4 text-sm leading-6 text-slate-300">
            <ShieldCheckIcon className="mt-0.5 shrink-0 text-[#F5C400]" size={15} aria-hidden="true" />
            <p>
              Exempelvy i en lokal prototyp. Inget ID verifieras, delas eller
              används som riktig handling.
            </p>
          </div>
        </div>
        <div className="min-w-0 bg-[#EAF5F8] p-4 sm:p-5">
          <div className="mx-auto w-full max-w-sm rounded-xl border border-[#06B6D4]/25 bg-[#F9FEFF] p-3.5 shadow-[0_5px_14px_rgba(11,17,32,0.05)]">
            <div className="flex items-center justify-between gap-3 border-b border-[#0B1120]/10 pb-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#0B1120] text-[#67E8F9]">
                  <WalletCardsIcon size={16} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-[#0B1120]">DG-plånbok</p>
                  <p className="truncate text-xs text-slate-700">Digitala ID · exempel</p>
                </div>
              </div>
              <span className="shrink-0 rounded-md border border-[#06B6D4]/40 bg-white px-2 py-1 text-xs font-bold uppercase tracking-[.05em] text-[#006D98]">
                Lokal vy
              </span>
            </div>
            <div className="mt-3 rounded-xl border border-[#06B6D4]/30 bg-[#102C3F] p-3 text-white">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#67E8F9]/45 bg-[#0B1120] text-xs font-bold text-[#A5F3FC]">
                    AL
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold uppercase tracking-[.08em] text-[#CFFAFE]">
                      Förar-ID · exempel
                    </p>
                    <p className="mt-0.5 truncate text-sm font-bold">Alex Lind</p>
                    <p className="truncate text-xs text-slate-200">Taxiförare · Stockholm</p>
                  </div>
                </div>
                <BadgeCheckIcon className="shrink-0 text-[#F5C400]" size={18} aria-hidden="true" />
              </div>
            </div>
            <div className="mt-3 divide-y divide-[#0B1120]/10 rounded-xl border border-[#0B1120]/10 bg-white px-3">
              {credentials.map(({ label, detail, icon: Icon }) => (
                <div key={label} className="flex items-center gap-2.5 py-2.5">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#ECFEFF] text-[#007EAF]">
                    <Icon size={14} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-[#0B1120]">{label}</p>
                    <p className="truncate text-xs text-slate-700">{detail}</p>
                  </div>
                  <span className="shrink-0 rounded-md bg-[#DCFCE7] px-1.5 py-0.5 text-xs font-bold text-[#166534]">
                    Exempel
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-[#06B6D4]/25 bg-[#ECFEFF] px-3 py-2.5 text-[#164E63]">
              <span className="flex min-w-0 items-center gap-2 text-sm font-bold">
                <EyeIcon className="shrink-0" size={15} aria-hidden="true" />
                <span className="truncate">Visningsläge vid kontroll</span>
              </span>
              <span className="shrink-0 text-xs font-bold text-[#006D98]">Exempel</span>
            </div>
          </div>
        </div>
      </div>
    </motion.aside>
  );
}
